import { forwardRef, Ref, ReactElement, useState, useEffect } from "react";
import Select, {
  Props,
  SelectInstance as ReactSelectInstance,
  components as selectComponents,
  DropdownIndicatorProps,
  OptionProps,
  SingleValueProps,
} from "react-select";
import makeAnimated from "react-select/animated";
import IconSprite from "@/components/UI/IconSprite";

// Utils
import st from "./index.module.scss";

export type SelectItemType = {
  id: string | number;
  value: string;
  label: string;
  icon?: JSX.Element;
  field?: string;
  options?: SelectItemType[];
};

const components = {
  ...selectComponents,
  ...makeAnimated(),
};

const SingleValue = (selectProps: SingleValueProps<SelectItemType>) => (
  <components.SingleValue {...selectProps} className={st["select-item"]}>
    {selectProps.data?.icon && selectProps.data.icon} {selectProps.data?.label}
  </components.SingleValue>
);
const Option = (selectProps: OptionProps<SelectItemType>) => (
  <components.Option {...selectProps}>
    {selectProps.data?.icon && selectProps.data.icon} {selectProps.data?.label}
  </components.Option>
);
const DropdownIndicator = (selectProps: DropdownIndicatorProps<SelectItemType, false>) => {
  return (
    <components.DropdownIndicator {...selectProps}>
      <IconSprite name="separation" />
    </components.DropdownIndicator>
  );
};

export const SelectSorter = forwardRef(
  (
    selectProps: Props<SelectItemType>,
    ref: Ref<ReactSelectInstance<SelectItemType>>,
  ): ReactElement => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
      setMounted(true);
    }, []);

    if (!mounted) return <></>;

    return (
      <Select
        {...selectProps}
        className={st.select}
        classNamePrefix="react-select"
        ref={ref}
        instanceId={selectProps.name}
        components={{
          ...components,
          DropdownIndicator,
          SingleValue,
          Option,
          IndicatorSeparator: () => null,
        }}
        classNames={{
          control: (state) =>
            state.isFocused ? "select-control select-focused" : "select-control",
        }}
        styles={{
          control: (baseStyles, { isFocused }) => ({
            ...baseStyles,
            background: "none",
            borderColor: isFocused ? "grey" : "red",
            zIndex: 3,
            minHeight: "48px",
            cursor: "pointer",
            border: "2px solid var(--gray-500-p-40)",
            boxShadow: "none",
            borderRadius: "16px",
            minWidth: "208px",
            marginRight: "0",
            marginLeft: "auto",
            padding: "12px 8px 10px 12px",
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            background: "none",
            zIndex: 50,
            margin: 0,
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            background: "none",
            cursor: "pointer",
          }),
        }}
      />
    );
  },
);
SelectSorter.displayName = "SelectSorter";
